import { useReducer } from 'react';
import {
	type FullOrderViewModel,
	getGoalsAndInterventionsByCertificationPeriodOptions,
	addGoalWithInterventionsOptions,
	addInterventionsOptions,
	getGoalAndInterventionDefinitionLibraryOptions,
	InterventionDefinitionTypeEnum,
	DisciplineEnum
} from '@generated';
import { FormRegistryModal } from '~/shared/forms/FormRegistryModal';
import { useIdentity } from '~/features/authentication/identity/IdentityContext';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getInitialWizardState, wizardReducer } from './reducer/wizardReducer';
import type { FormRegistryValues } from '~/shared/forms/FormRegistryContext';
import { Breadcrumbs } from './Breadcrumbs';
import { Footer } from './Footer';
import { convertClientDateToServerDate } from '~/util/date';
import { WizardSteps } from './WizardSteps';
import { Header3 } from '~/shared/components/text/Header3';
import { GoalsAndInterventionsWizardContextProvider, useGoalsAndInterventionsWizard } from './GoalsAndInterventionsWizardContext';
import type { SuggestedGoal } from '../suggestions';
import { GoalCard } from '../goals/GoalCard';
import { CustomModalHeader } from '~/shared/components/modal/Modal';
import { transformWizardStateForSave } from './util';
import { useLoadDefinitions } from './useLoadDefinitions';
import { useGoalsAndnterventionsForOrder } from '../queries';

export function GoalAndInterventionsWizardFormModal({
	fullOrder,
	suggestedGoalsAndInterventions,
	closeModal
}: {
	fullOrder: FullOrderViewModel;
	suggestedGoalsAndInterventions?: SuggestedGoal[];
	closeModal: () => void;
}) {
	const { loggedInUser } = useIdentity();
	const { refetch } = useSuspenseQuery(useGoalsAndnterventionsForOrder(fullOrder.id));
	const { data: goalsAndInterventions } = useSuspenseQuery(getGoalsAndInterventionsByCertificationPeriodOptions(fullOrder.certificationPeriodId));
	const { data: goalAndInterventionDefinitionLibrary } = useSuspenseQuery(
		getGoalAndInterventionDefinitionLibraryOptions(convertClientDateToServerDate(fullOrder.certificationPeriod.startDate))
	);

	const currentUserDisciplineId = loggedInUser.clinicalTypeDisciplineId ?? DisciplineEnum.SkilledNursing;

	const existingCustomInterventionCount = goalsAndInterventions.interventions.filter(
		x => x.interventionDefinitionTypeId === InterventionDefinitionTypeEnum.Custom
	).length;

	const [wizardState, dispatch] = useReducer(
		wizardReducer,
		getInitialWizardState(
			!!suggestedGoalsAndInterventions && suggestedGoalsAndInterventions.length > 0,
			currentUserDisciplineId,
			existingCustomInterventionCount
		)
	);

	const definitionsLoading = useLoadDefinitions(wizardState, dispatch, fullOrder);

	return (
		<GoalsAndInterventionsWizardContextProvider
			wizardState={wizardState}
			dispatch={dispatch}
			fullOrder={fullOrder}
			goalsAndInterventions={goalsAndInterventions}
			suggestedGoalsAndInterventions={suggestedGoalsAndInterventions ?? []}
			goalAndInterventionDefinitionLibrary={goalAndInterventionDefinitionLibrary}
			defaultAssignedDisciplineId={currentUserDisciplineId}
		>
			<FormRegistryModal
				headerSlot={
					<CustomModalHeader closeModal={closeModal} titleClassName="flex flex-row items-center justify-between">
						{closeModal => (
							<>
								<div className="flex flex-col gap-2">
									<Header3>Goals &amp; Interventions</Header3>
									<Breadcrumbs />
								</div>
								<div className="flex flex-row items-center gap-4">
									<DisplaySelectedGoal />
									{closeModal}
								</div>
							</>
						)}
					</CustomModalHeader>
				}
				onSaveTransform={(formRegistryValues: FormRegistryValues) =>
					transformWizardStateForSave(fullOrder.id, wizardState, formRegistryValues)
				}
				saveMutation={wizardState.selectedGoalOption === 'existing' ? addInterventionsOptions : addGoalWithInterventionsOptions}
				onSuccess={() => {
					refetch();
					closeModal();
				}}
				closeModal={closeModal}
				submitButton={<Footer />}
				size="xl"
				modalClassName="h-[90vh]"
				isPending={definitionsLoading}
			>
				<WizardSteps />
			</FormRegistryModal>
		</GoalsAndInterventionsWizardContextProvider>
	);
}

function DisplaySelectedGoal() {
	const { wizardState } = useGoalsAndInterventionsWizard();
	if (!wizardState.selectedGoal) {
		return null;
	}

	return (
		<>
			<span className="text-sm text-gray-500">Selected Goal:</span>
			{wizardState.selectedGoal.type === 'existing' ? (
				<GoalCard title={wizardState.selectedGoal.existingGoal.title} definitionText={wizardState.selectedGoal.existingGoal.definitionText} />
			) : wizardState.selectedGoal.type === 'custom' ? (
				<GoalCard title={wizardState.selectedGoal.formValues?.subject ?? ''} definitionText={wizardState.selectedGoal.formValues?.definitionText ?? ''} />
			) : (
				<GoalCard
					title={wizardState.selectedGoal.goalDefinition.title}
					definitionText={wizardState.selectedGoal.goalDefinition.definitionText}
				/>
			)}
		</>
	);
}
